import { useTranslation } from 'react-i18next'
import { cn } from '@/lib/utils'
import { TagChip, type TagChipProps } from './TagChip'

export interface Tag {
  id: string
  label: string
}

export interface TagListProps {
  tags: Tag[]
  selectedIds?: Set<string>
  onTagClick?: (tag: Tag) => void
  onTagRemove?: (tag: Tag) => void
  variant?: TagChipProps['variant']
  size?: TagChipProps['size']
  iconOnly?: boolean
  maxVisible?: number
  className?: string
}

export function TagList({
  tags,
  selectedIds = new Set(),
  onTagClick,
  onTagRemove,
  variant = 'default',
  size = 'md',
  iconOnly = false,
  maxVisible,
  className,
}: TagListProps) {
  const { t } = useTranslation()
  const visibleTags = maxVisible ? tags.slice(0, maxVisible) : tags
  const hiddenCount = maxVisible ? tags.length - maxVisible : 0

  if (tags.length === 0) {
    return null
  }

  return (
    <div className={cn('flex flex-wrap', iconOnly ? 'gap-1' : 'gap-2', className)}>
      {visibleTags.map((tag) => (
        <TagChip
          key={tag.id}
          label={tag.label}
          tagId={tag.id}
          selected={selectedIds.has(tag.id)}
          onClick={onTagClick ? () => onTagClick(tag) : undefined}
          onRemove={onTagRemove ? () => onTagRemove(tag) : undefined}
          variant={variant}
          size={size}
          iconOnly={iconOnly}
        />
      ))}
      {hiddenCount > 0 && (
        <span
          className={cn(
            'inline-flex items-center rounded-full font-medium text-gray-500 dark:text-gray-400',
            size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-sm px-2.5 py-1'
          )}
        >
          {t('components.tag_list.more_count', { count: hiddenCount })}
        </span>
      )}
    </div>
  )
}

export default TagList
